/**
 * Thin fetch wrapper for /api/* calls.
 *
 * Responsibilities:
 * - JSON encode/decode.
 * - Surface 401s as a typed `UnauthenticatedError` so the router can
 *   redirect to the "Not signed in" view.
 * - Surface 5xx as a typed `ServerError` so the global error
 *   boundary / toast layer can show a non-destructive notification.
 * - Pass-through for 4xx (other than 401) so callers can render
 *   field-level errors when those endpoints land in later slices.
 *
 * Slice 3 only needs GET /api/me. The wrapper is structured so
 * slice 4 (#110) can add operations endpoints without reshaping
 * this layer.
 */

export class UnauthenticatedError extends Error {
  readonly status = 401;
  constructor(public readonly reason?: string) {
    super(`unauthenticated${reason ? `: ${reason}` : ""}`);
    this.name = "UnauthenticatedError";
  }
}

export class ServerError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(`server error ${status}`);
    this.name = "ServerError";
  }
}

export class ClientError extends Error {
  /**
   * Slice 8 (#114) — the worker's 4xx envelope is `{ error, reason, ... }`.
   * Surface `reason` directly so callers (TanStack mutation hooks, toast
   * layer) can render a user-readable message without re-parsing.
   */
  public readonly reason: string | undefined;
  constructor(
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(`client error ${status}`);
    this.name = "ClientError";
    this.reason =
      body && typeof body === "object" && "reason" in body &&
      typeof (body as { reason?: unknown }).reason === "string"
        ? (body as { reason: string }).reason
        : undefined;
  }
}

/**
 * Map a fetch Response to the shared error taxonomy
 * (UnauthenticatedError | ServerError | ClientError) or return the
 * parsed JSON body. Shared by `apiGet` and `apiPost`.
 */
async function handleResponse<T>(res: Response): Promise<T> {
  if (res.status === 401) {
    let reason: string | undefined;
    try {
      const body = (await res.json()) as { reason?: string };
      reason = body.reason;
    } catch {
      // Body wasn't JSON; ignore.
    }
    throw new UnauthenticatedError(reason);
  }

  if (res.status >= 500) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      body = await res.text().catch(() => null);
    }
    throw new ServerError(res.status, body);
  }

  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      body = await res.text().catch(() => null);
    }
    throw new ClientError(res.status, body);
  }

  return (await res.json()) as T;
}

export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(path, {
    method: "GET",
    headers: { Accept: "application/json" },
    credentials: "include",
  });
  return handleResponse<T>(res);
}

/**
 * Generic JSON POST helper. Same error taxonomy as apiGet. Body is
 * JSON-encoded; caller-supplied headers (e.g., `Idempotency-Key`)
 * merge over the defaults so a mutation hook can attach a per-attempt
 * UUID without rebuilding the fetch invocation.
 *
 * Platform plumbing — kept across the 2026-05-18 Command Center reset.
 * The write surfaces that used it were wiped; rebuilt surfaces call it.
 */
export async function apiPost<T>(
  path: string,
  body: unknown,
  opts?: { headers?: Record<string, string> },
): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(opts?.headers ?? {}),
    },
    credentials: "include",
    body: JSON.stringify(body),
  });
  return handleResponse<T>(res);
}

/**
 * Generic JSON PATCH helper. Mirrors `apiPost` exactly: same error
 * taxonomy, same JSON encoding, same caller-supplied-header merge (a
 * partial field update is naturally idempotent, so callers don't
 * normally need an Idempotency-Key on PATCH — but the merge keeps the
 * signatures uniform). Platform plumbing, kept across the reset.
 */
export async function apiPatch<T>(
  path: string,
  body: unknown,
  opts?: { headers?: Record<string, string> },
): Promise<T> {
  const res = await fetch(path, {
    method: "PATCH",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(opts?.headers ?? {}),
    },
    credentials: "include",
    body: JSON.stringify(body),
  });
  return handleResponse<T>(res);
}

/**
 * Generic JSON PUT helper. Mirrors `apiPatch` exactly (same error
 * taxonomy, JSON encoding, header merge) but with `method: "PUT"` for a
 * full-resource replace rather than a partial update. Used by the digest
 * template editor (#488), which PUTs the whole templates blob.
 */
export async function apiPut<T>(
  path: string,
  body: unknown,
  opts?: { headers?: Record<string, string> },
): Promise<T> {
  const res = await fetch(path, {
    method: "PUT",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(opts?.headers ?? {}),
    },
    credentials: "include",
    body: JSON.stringify(body),
  });
  return handleResponse<T>(res);
}

/**
 * Generic DELETE helper. No body; same error taxonomy as apiGet/apiPost.
 * Used by the firm-document delete route (ADR-0086).
 */
export async function apiDelete<T>(path: string): Promise<T> {
  const res = await fetch(path, {
    method: "DELETE",
    headers: { Accept: "application/json" },
    credentials: "include",
  });
  return handleResponse<T>(res);
}

/**
 * Generic multipart upload helper. Single-file POST with
 * `multipart/form-data`. No Content-Type header — the browser fills it in
 * (with the boundary) when given a FormData body. Used by the firm-document
 * upload route (ADR-0086).
 */
export async function apiUpload<T>(
  path: string,
  field: string,
  file: File,
): Promise<T> {
  const body = new FormData();
  body.append(field, file, file.name);
  const res = await fetch(path, {
    method: "POST",
    headers: { Accept: "application/json" },
    credentials: "include",
    body,
  });
  return handleResponse<T>(res);
}
